import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {CreateInviteRequest} from "@/model/request/invite/CreateInviteRequest.ts";
import {ActionType} from "@/util/type/store.ts";
import {InviteResponse} from "@/model/response/InviteResponse.ts";
import {InviteService} from "@/service/InviteService.ts";
import {VerifyInviteResponse, VerifyInviteResponseData} from "@/model/response/invite/VerifyInviteResponse.ts";
import {VerifyInviteRequest} from "@/model/request/invite/VerifyInviteRequest.ts";
import {ReadInviteResponse, ReadInviteResponseData} from "@/model/response/invite/ReadInviteResponse.ts";
import {InviteRegistrationRequest} from "@/model/request/invite/InviteRegistrationRequest.ts";
import {BaseResponse} from "@/model/response/BaseResponse.ts";
import {ResendPendingInviteRequest} from "@/model/request/invite/ResendPendingInviteRequest.ts";
import {ResendPendingInviteResponse} from "@/model/response/invite/ResendPendingInviteResponse.ts";

export type InviteState = {
    loading: boolean;
    readInvite: ReadInviteResponseData[];
    verifiedInviteResponse: VerifyInviteResponseData
};

const initialState: InviteState = {
    loading: false,
    verifiedInviteResponse: {} as VerifyInviteResponseData,
    readInvite: [] as ReadInviteResponseData[],
};

const action = {
    createInvite: createAsyncThunk(
        "invite/createInvite",
        async (payload: ActionType<CreateInviteRequest>, thunkAPI) => {
            return (await InviteService.invite(thunkAPI, payload.data)).data;
        }
    ),
    verifyInvite: createAsyncThunk(
        "invite/verifyInvite", async (payload: VerifyInviteRequest, thunkAPI) => {
            return (await InviteService.verifyInvite(thunkAPI, payload)).data
        }),
    readInvite: createAsyncThunk(
        "invite/readInvite", async (_, thunkAPI) => {
            return (await InviteService.readInvite(thunkAPI)).data
        }),
    inviteRegistration: createAsyncThunk(
        "invite/inviteRegistration", async (payload: ActionType<InviteRegistrationRequest>, thunkAPI) => {
            return (await InviteService.inviteRegistration(thunkAPI, payload.data)).data
        }),
    resendInvite: createAsyncThunk(
        "invite/resendInvite", async (payload: ActionType<ResendPendingInviteRequest>, thunkAPI) => {
            return (await InviteService.resendInvite(thunkAPI, payload.data)).data
        })
};

const slice = createSlice({
    name: "invite",
    initialState,
    reducers: {
        setLoading(state, {payload}) {
            state.loading = payload;
        },
        reset: () => ({...initialState})
    },
    extraReducers: (builder) => {
        builder
            .addCase(action.createInvite.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.createInvite.fulfilled, (state, action) => {
                const response = action.payload as InviteResponse;
                state.loading = false;
                if (response.responseCode === "00")
                    action.meta.arg.onSuccess?.(action.payload);
                else {
                    action.meta.arg.onError?.(action.payload);
                }
            }).addCase(action.verifyInvite.pending, (state) => {
            state.loading = true
        }).addCase(action.verifyInvite.fulfilled, (state, action) => {
            const response = action.payload as VerifyInviteResponse;
            state.loading = false
            if (response.responseCode === "00") {
                state.verifiedInviteResponse = response.data
            }
        }).addCase(action.readInvite.pending, (state) => {
            state.loading = true
        }).addCase(action.readInvite.fulfilled, (state, action) => {
            const response = action.payload as ReadInviteResponse;
            state.loading = false
            if (response.responseCode === "00") {
                state.readInvite = response.data
            }
        }).addCase(action.resendInvite.pending, (state) => {
            state.loading = true
        }).addCase(action.resendInvite.fulfilled, (state, action) => {
            const response = action.payload as ResendPendingInviteResponse;
            state.loading = false
            if (response.responseCode == "00") {
                action.meta.arg.onSuccess?.(action.payload);
            } else {
                action.meta.arg.onError?.(action.payload);
            }
        })
            .addCase(action.inviteRegistration.pending, (state) => {
                state.loading = true
            }).addCase(action.inviteRegistration.fulfilled, (state, action) => {
            const response = action.payload as BaseResponse;
            state.loading = false
            if (response.responseCode === "00") {
                action.meta.arg.onSuccess?.(action.payload);
            } else {
                action.meta.arg.onError?.(action.payload)
            }
        });
    },
});

export const invite = {
    reducer: slice.reducer,
    action: action,
    mutation: slice.actions,
};
